import { ref, computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useAuthStore } from '../pinia-stores/auth.js';
import { useAccountStore } from '../pinia-stores/account.js';

/**
 * Minimal payment-form state for the unified payment page.
 *
 * The legacy implementation (deleted in b753473) also owned the PIN keypad,
 * intl-tel-input instances and the legacy payWith, QR and network flows. Those
 * responsibilities now live elsewhere:
 *   - keypad/authentication  -> useTransactAuth / TransactAuthPanel
 *   - intl-tel-input fields  -> useTelephone (in payment.vue)
 *   - initiation/execution   -> paymentTxStore / payment.vue
 *
 * This composable only supplies the refs/computeds the form binds and the
 * `setPhoneNumber()` prefill helper.
 */
export function usePayment(options = {}) {
  const { f7route, f7router } = options; // kept for API compatibility

  const authStore = useAuthStore();
  const accountStore = useAccountStore();

  const baseTransfer = ref(null);
  const payment_detail = ref(null);
  const paymentCurrency = ref('');
  const selected_method = ref('');
  const sender_wallet_id = ref('');
  const telephone = ref('');
  const lock_phone_number = ref(false);
  const selectedCountry = ref('');
  const firstName = ref('');
  const lastName = ref('');
  const email = ref('');
  const address = ref('');
  const city = ref('');
  const state = ref('');
  const countryCode = ref('');
  const postalCode = ref('');

  const userAccount = computed(() => accountStore.userAccount);
  const userDetail = computed(() => accountStore.userDetail);

  const senderAvailableWallets = computed(() => {
    const wallets = userAccount.value?.userAccountWallets
      ? Object.keys(userAccount.value.userAccountWallets).flatMap(
          (type) => userAccount.value.userAccountWallets[type] || []
        )
      : [];
    const currency = payment_detail.value?.paymentCurrency ?? baseTransfer.value?.currency;
    return wallets.filter(
      (w) =>
        !w.shared &&
        w.id !== baseTransfer.value?.receiver_wallet_id &&
        (currency ? w.currency === currency : true)
    );
  });

  const stripeAccountId = computed(() => {
    if (userAccount.value?.standard?.id) return userAccount.value.standard?.stripeAccountId;
    if (userAccount.value?.business?.id) return userAccount.value.business?.stripeAccountId;
    return null;
  });

  function fillStandardDetails() {
    const std = userAccount.value?.standard;
    if (!std) return;
    const { standard_info, address: addr } = std;
    selectedCountry.value = baseTransfer.value?.countryCode || userAccount.value.countryCode;
    telephone.value =
      selectedCountry.value === userAccount.value.countryCode ? standard_info.phone_number : '';
    firstName.value = standard_info.subscriber?.firstname || '';
    lastName.value = standard_info.subscriber?.lastname || '';
    email.value = standard_info.email || '';
    address.value = [addr?.label1, addr?.label2, addr?.label3, addr?.city, addr?.zip_code, addr?.country]
      .filter((part) => part && part.trim() !== '')
      .join(' ');
    city.value = addr?.city || '';
    state.value = userAccount.value.countryCode || '';
    countryCode.value = userAccount.value.countryCode || '';
    postalCode.value = addr?.zip_code || '';
  }

  function fillBusinessDetails() {
    const biz = userAccount.value?.business;
    if (!biz) return;
    const { business_info, address: addr } = biz;
    selectedCountry.value = baseTransfer.value?.countryCode || userAccount.value.countryCode;
    telephone.value =
      selectedCountry.value === userAccount.value.countryCode ? business_info.phone_number : '';
    firstName.value = business_info.name || '';
    lastName.value = business_info.description || '';
    email.value = business_info.email || '';
    address.value = [addr?.label1, addr?.label2, addr?.label3, addr?.city, addr?.zip_code, addr?.country]
      .filter((part) => part && part.trim() !== '')
      .join(' ');
    city.value = addr?.city || '';
    state.value = userAccount.value.countryCode || '';
    countryCode.value = userAccount.value.countryCode || '';
    postalCode.value = addr?.zip_code || '';
  }

  /**
   * Prefills payer/beneficiary details from the authenticated account and the
   * chosen payment method. Only touches reactive state — the intl-tel-input
   * fields are (re)synced by payment.vue through the useTelephone instances.
   *
   * @param {object|null} method the selected payment-method object, passed so
   *   the original `paymentPartner === 'cinetpay'` branch stays faithful.
   */
  function setPhoneNumber(method = null) {
    if (!baseTransfer.value) return;
    const { transaction_type, payer_account_id, countryCode: cc } = baseTransfer.value;
    if (transaction_type === 'CASH_IN') {
      if (userAccount.value?.standard?.id === payer_account_id) {
        fillStandardDetails();
      } else if (userAccount.value?.business?.id === payer_account_id) {
        fillBusinessDetails();
      }
      if (selected_method.value === 'CREDIT_CARD') {
        fillStandardDetails();
      }
    } else if (
      transaction_type === 'CASH_OUT' &&
      String(method?.paymentPartner || '').toUpperCase() === 'CINETPAY' &&
      ['AVOCASH', 'MOBILE_MONEY', 'WALLET'].includes(selected_method.value)
    ) {
      const std = userDetail.value?.standard?.standard_info;
      if (std) {
        firstName.value = std.subscriber?.firstname || firstName.value;
        lastName.value = std.subscriber?.lastname || lastName.value;
        email.value = std.email || email.value;
      }
      if (userDetail.value && cc === userDetail.value.countryCode) {
        telephone.value = userDetail.value.username || '';
        selectedCountry.value = userDetail.value.countryCode || selectedCountry.value;
      } else {
        telephone.value = '';
        selectedCountry.value = cc || selectedCountry.value;
      }
    }
  }

  return {
    baseTransfer,
    payment_detail,
    paymentCurrency,
    selected_method,
    sender_wallet_id,
    telephone,
    lock_phone_number,
    selectedCountry,
    firstName,
    lastName,
    email,
    address,
    city,
    state,
    countryCode,
    postalCode,
    userAccount,
    userDetail,
    senderAvailableWallets,
    stripeAccountId,
    setPhoneNumber,
  };
}